import React, {Fragment, PropsWithChildren, useCallback, useContext, useMemo} from 'react';
import {RsnDialog, Skeleton} from '..';
import {useOptionalControls, useShallowMemo} from '../../Hooks';
import {color} from '../../Theme';
import {cva, styles} from '../../Utilities/defineStyles';
import {FieldLabel, FieldLabelWithInfo} from '../_internal/FieldLabel/FieldLabel';
import {FieldLabelProps} from '../_internal/FieldLabel/FieldLabel.types';
import {IconProp} from '../shared/props';
import {RsnSelectSearch} from './RsnSelect.Search';
import {
    DropdownProps,
    InternalControls,
    OptionObject,
    RsnSelectProps,
    DropdownProps as _DropdownProps,
} from './RsnSelect.types';

export function RsnSelect<T>({
    onChange,
    onClear,
    value,
    options,
    disabled,
    name,
    onBlur,
    onFocus,

    label,
    issue,
    helperText,
    isInvalid,
    withIcon,
    placeholder = 'Select option',

    triggerAttributes,
    triggerRef,

    renderDropdown = renderDefaultDropdown,
    opened: dropdownIsOpen,
    onDropdownToggle: onDropdownToggle,
}: RsnSelectProps<T>) {
    if (issue) {
        helperText = issue;
        isInvalid = true;
    }
    const selectedOption = useMemo(() => options?.find(option => option.value === value), [value, options]);
    const isReady = options && options.length > 0;

    const controls = useOptionalControls(false, dropdownIsOpen, onDropdownToggle);
    const [isOpened, setIsOpened] = (() => {
        const [value, setValue] = controls;
        const setters = {
            on() {
                if (isReady && !disabled) setValue(true);
            },
            off() {
                setValue(false);
            },
        };
        return [value, setters] as const;
    })();

    const header = (() => {
        if (label == null) {
            return placeholder;
        } else if (typeof label == 'string') {
            return label;
        } else if ('props' in label && 'children' in label.props) {
            return label.props.children;
        }
    })();

    return (
        <div className={cls.outerBox({disabled, isInvalid})}>
            {label}
            <button
                {...triggerAttributes}
                type='button'
                className={cls.button}
                ref={triggerRef}
                name={name}
                onBlur={onBlur}
                onFocus={onFocus}
                onClick={setIsOpened.on}
                disabled={disabled}
            >
                <div className={cls.innerBox}>
                    {!isReady
                        ? renderLoadingPlaceholder()
                        : !selectedOption
                          ? renderNothingSelectedPlaceholder()
                          : renderSelectedOption(selectedOption)}

                    <div className={cls.decor}>
                        <ArrowDown />
                    </div>
                </div>
            </button>
            <div className={cls.helper}>{helperText}</div>

            <ControlsProvider value={value} onChange={onChange} onClear={onClear} onClose={setIsOpened.off}>
                <RsnDialog.Controller onClose={setIsOpened.off}>
                    {isReady && isOpened && renderDropdown({options, header})}
                </RsnDialog.Controller>
            </ControlsProvider>
        </div>
    );

    function renderLoadingPlaceholder() {
        return (
            <Fragment>
                {withIcon && <Skeleton circle={20} />}
                <Skeleton width={100} height={12} borderRadius={6} />
            </Fragment>
        );
    }

    function renderSelectedOption(selectedOption: OptionObject<T>) {
        let iconElement: React.JSX.Element | null = null;
        if (withIcon && selectedOption.icon) {
            iconElement = <div className={cls.icon}>{IconProp.render(selectedOption.icon)}</div>;
        }
        return (
            <Fragment>
                {iconElement}
                <div className={cls.optionText}>{selectedOption.label}</div>
            </Fragment>
        );
    }

    function renderNothingSelectedPlaceholder() {
        return (
            <Fragment>
                <div className={cls.placeholder}>{DASH + placeholder + DASH}</div>
            </Fragment>
        );
    }
}

export namespace RsnSelect {
    export type Props<T> = RsnSelectProps<T>;
    export type DropdownProps<T> = _DropdownProps<T>;
    export type LabelProps = FieldLabelProps;
    export type Option<T> = OptionObject<T>;

    export const Label = FieldLabel;
    export const LabelWithInfo = FieldLabelWithInfo;
    export const Option = RsnSelectOption;
    export const Search = RsnSelectSearch;
    export const useControls = useRsnSelectControls;
}

export function getKey(option: OptionObject<any>) {
    return option.key ?? option.label;
}

function renderDefaultDropdown<T>({header, options}: DropdownProps<T>) {
    return (
        <RsnDialog.Window>
            <RsnDialog.LeftHeader>{header}</RsnDialog.LeftHeader>
            <RsnDialog.Body padding='y'>
                {options.map(option => (
                    <RsnSelectOption key={getKey(option)} option={option} />
                ))}
            </RsnDialog.Body>
        </RsnDialog.Window>
    );
}

export interface OptionProps<T> {
    option: OptionObject<T>;
    children?: React.ReactNode;
}

export function RsnSelectOption<T>({option, children}: OptionProps<T>) {
    const {value, onChange, onClear, onClose} = useRsnSelectControls(option);
    const isSelected = value === option.value;

    return (
        <button
            className={cls.option}
            onClick={ev => {
                if (isSelected && onClear) {
                    onClear();
                } else {
                    onChange(option.value, ev);
                }
                onClose();
            }}
        >
            {option.icon && <figure className='size-9 min-w-[36px]'>{IconProp.render(option.icon)}</figure>}
            <div className={cls.optionText}>{children ?? option.label}</div>
            {isSelected ? <CheckIcon /> : null}
        </button>
    );
}

const cls = styles({
    icon: 'size-5',
    decor: 'ml-auto size-4 group-[.off]:hidden',
    helper: 'absolute left-0 top-full pt-1 text-neutral-60 group-[.bad]:text-danger-30',
    placeholder: 'text-shade-60',
    option: 'flex h-14 w-full items-center gap-2 px-4 py-2 text-neutral-100 hover:bg-neutral-20 active:bg-neutral-30 disabled:text-neutral-60',
    optionText: 'fv-text-xl mr-auto overflow-hidden text-ellipsis text-nowrap text-left [&_b]:fv-heading-s',
    button: [
        'fv-text-xl h-11 w-full overflow-hidden rounded-sm border border-solid border-neutral-50 px-3',
        'text-neutral-90 ring-primary-20 focus:border-primary-30 focus:text-neutral-100 focus:ring',
        'disabled:color-neutral-60 disabled:border-neutral-50 disabled:bg-neutral-30 disabled:text-neutral-60',
        'group-[.bad]:border-danger-30 group-[.bad]:ring-danger-20',
    ],
    innerBox: 'mx-0.5 flex items-center gap-2',
    outerBox: cva('group fv-text-m relative grid gap-2', {
        variants: {
            disabled: {true: 'off', false: ''},
            isInvalid: {true: '', false: ''},
        },
        defaultVariants: {disabled: false},
        compoundVariants: [{isInvalid: true, disabled: false, className: 'bad'}],
    }),
});

const ControlsContext = React.createContext<() => InternalControls<any>>(() => {
    throw new Error('RsnSelect composite components should not be used without top-level RsnSelect');
});

function ControlsProvider({children, value, onChange, onClear, onClose}: PropsWithChildren<InternalControls<any>>) {
    const controls = useShallowMemo({value, onChange, onClear, onClose});
    return (
        <ControlsContext.Provider value={useCallback(() => controls, [controls])}>{children}</ControlsContext.Provider>
    );
}

function useRsnSelectControls<T>(
    /**
     * Contexts can't be generic, instead of passing type parameter directly (which would be equivalent to type casting)
     * slightly better approach is to pass an option object you might already be working with to let typescript infer T from it.
     */
    __optionToInferFrom: OptionObject<T>
): InternalControls<T> {
    const controls = useContext(ControlsContext)();
    return controls;
}

const ArrowDown = React.memo(function ArrowDown() {
    return (
        <svg className='size-4' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'>
            <path fill={color.neutral90} d='m12 13.172 4.95 -4.95 1.414 1.414 L12 16 5.636 9.636 7.05 8.222' />
        </svg>
    );
});

const CheckIcon = React.memo(function CheckIcon() {
    return (
        <svg className='size-5' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'>
            <path
                d='m10 15.172 9.192-9.193 1.415 1.414L10 18l-6.364-6.364 1.414-1.414 4.95 4.95Z'
                fill={color.primary30}
            />
        </svg>
    );
});

const DASH = String.fromCharCode(8211);
